import type { Novel } from "./types.js";

export const demoNovel: Novel = {
  schema_version: 1,
  version: 1,
  id: "novel_parallel_classroom_demo",
  title: "Parallel Classroom",
  description: "A school mystery about a classroom that exists between realities.",
  visibility: "public",
  language: "en",
  genre: ["mystery", "school", "supernatural"],
  tone: "dark",
  cover_asset_id: "asset_cover_parallel_classroom",
  start_scene_id: "scene_001",
  characters: [
    {
      id: "char_mira",
      name: "Mira",
      gender: "female",
      age: 17,
      traits: ["Reserved", "Curious", "Secretive"],
      appearance: "black hair, school uniform, tired eyes",
      portrait_asset_id: "asset_portrait_mira"
    },
    {
      id: "char_ren",
      name: "Ren",
      gender: "male",
      age: 17,
      traits: ["Curious", "Stubborn", "Meticulous"],
      appearance: "messy brown hair, school uniform, notebook in hand",
      portrait_asset_id: "asset_portrait_ren"
    }
  ],
  scenes: [
    {
      id: "scene_001",
      title: "The Empty Classroom",
      background_asset_id: "asset_bg_classroom_night",
      music_asset_id: "asset_music_low_pulse",
      characters_present: ["char_ren", "char_mira"],
      text: "The bell rings, but nobody moves. Outside the window, the city has disappeared behind a wall of white light. Mira watches the door as if it might breathe.",
      choices: [
        {
          id: "choice_001_talk_mira",
          text: "Talk to Mira",
          next_scene_id: "scene_002"
        },
        {
          id: "choice_001_open_door",
          text: "Open the classroom door",
          next_scene_id: "scene_003"
        }
      ],
      is_ending: false
    },
    {
      id: "scene_002",
      title: "Mira's Warning",
      background_asset_id: "asset_bg_classroom_night",
      music_asset_id: "asset_music_low_pulse",
      characters_present: ["char_ren", "char_mira"],
      text: "Mira says this classroom appears whenever someone regrets the same choice twice. She points to the chalkboard, where your name is written in dust.",
      choices: [
        {
          id: "choice_002_read_board",
          text: "Read the chalkboard",
          next_scene_id: "scene_004"
        },
        {
          id: "choice_002_ignore_warning",
          text: "Ignore her warning and leave",
          next_scene_id: "scene_003"
        }
      ],
      is_ending: false
    },
    {
      id: "scene_003",
      title: "The Hallway Loop",
      background_asset_id: "asset_bg_looping_hallway",
      music_asset_id: "asset_music_hallway_echo",
      characters_present: ["char_ren"],
      text: "The hallway repeats in both directions. Every classroom door shows the same room, the same desks, and the same pale sky outside.",
      choices: [
        {
          id: "choice_003_return",
          text: "Return to Mira",
          next_scene_id: "scene_002"
        },
        {
          id: "choice_003_keep_walking",
          text: "Keep walking until the bell stops",
          next_scene_id: "scene_006"
        }
      ],
      is_ending: false
    },
    {
      id: "scene_004",
      title: "The Lesson Plan",
      background_asset_id: "asset_bg_chalkboard",
      music_asset_id: "asset_music_revelation",
      characters_present: ["char_ren", "char_mira"],
      text: "The chalkboard lists three names under tomorrow's date. Yours is crossed out. Mira admits she crossed it out after seeing you vanish in an earlier loop.",
      choices: [
        {
          id: "choice_004_trust_mira",
          text: "Trust Mira and follow the erased path",
          next_scene_id: "scene_005"
        },
        {
          id: "choice_004_accuse_mira",
          text: "Accuse Mira of trapping you here",
          next_scene_id: "scene_006"
        }
      ],
      is_ending: false
    },
    {
      id: "scene_005",
      title: "Morning Returns",
      background_asset_id: "asset_bg_sunrise_classroom",
      music_asset_id: "asset_music_sunrise",
      characters_present: ["char_ren", "char_mira"],
      text: "You erase the last line of the lesson plan together. The white light folds into sunrise, and the first students arrive without noticing the extra desk in the back.",
      choices: [],
      is_ending: true
    },
    {
      id: "scene_006",
      title: "The Forgotten Student",
      background_asset_id: "asset_bg_empty_desk",
      music_asset_id: "asset_music_low_pulse",
      characters_present: ["char_ren"],
      text: "The bell stops. Your desk becomes clean, your books lose your name, and the classroom quietly prepares to repeat the lesson with someone else.",
      choices: [],
      is_ending: true
    }
  ],
  endings: [
    {
      id: "ending_morning_returns",
      scene_id: "scene_005",
      title: "Morning Returns",
      type: "good",
      description: "Ren and Mira escape the parallel classroom with their memories intact."
    },
    {
      id: "ending_forgotten_student",
      scene_id: "scene_006",
      title: "The Forgotten Student",
      type: "bad",
      description: "Ren remains trapped as the classroom erases him from the world."
    }
  ],
  assets: [
    {
      id: "asset_cover_parallel_classroom",
      type: "cover",
      url: "/assets/placeholders/parallel-classroom-cover.svg",
      prompt: "Dark school classroom divided by white parallel reality light",
      status: "ready"
    },
    {
      id: "asset_portrait_mira",
      type: "portrait",
      url: "/assets/placeholders/mira.svg",
      prompt: "Calm schoolgirl with black hair and tired eyes, visual novel portrait",
      status: "ready"
    },
    {
      id: "asset_portrait_ren",
      type: "portrait",
      url: "/assets/placeholders/ren.svg",
      prompt: "Curious schoolboy with notebook, visual novel portrait",
      status: "ready"
    },
    {
      id: "asset_bg_classroom_night",
      type: "background",
      url: "/assets/placeholders/classroom-night.svg",
      prompt: "Empty classroom at night with vanished city outside",
      status: "ready"
    },
    {
      id: "asset_bg_looping_hallway",
      type: "background",
      url: "/assets/placeholders/looping-hallway.svg",
      prompt: "School hallway repeating into impossible distance",
      status: "ready"
    },
    {
      id: "asset_bg_chalkboard",
      type: "background",
      url: "/assets/placeholders/chalkboard.svg",
      prompt: "Classroom chalkboard with names written in dust",
      status: "ready"
    },
    {
      id: "asset_bg_sunrise_classroom",
      type: "background",
      url: "/assets/placeholders/sunrise-classroom.svg",
      prompt: "Classroom at sunrise as reality returns",
      status: "ready"
    },
    {
      id: "asset_bg_empty_desk",
      type: "background",
      url: "/assets/placeholders/empty-desk.svg",
      prompt: "Lonely empty school desk in a dark classroom",
      status: "ready"
    },
    {
      id: "asset_music_low_pulse",
      type: "music",
      url: "/assets/placeholders/audio-placeholder.txt",
      prompt: "Low ominous pulse loop",
      status: "ready"
    },
    {
      id: "asset_music_hallway_echo",
      type: "music",
      url: "/assets/placeholders/audio-placeholder.txt",
      prompt: "Echoing hallway ambient loop",
      status: "ready"
    },
    {
      id: "asset_music_revelation",
      type: "music",
      url: "/assets/placeholders/audio-placeholder.txt",
      prompt: "Quiet revelation music cue",
      status: "ready"
    },
    {
      id: "asset_music_sunrise",
      type: "music",
      url: "/assets/placeholders/audio-placeholder.txt",
      prompt: "Soft sunrise resolution loop",
      status: "ready"
    }
  ],
  owner_id: "system",
  created_by: "user_demo",
  created_at: "2026-05-22T00:00:00Z",
  updated_at: "2026-05-22T00:00:00Z",
  generation_input: {
    idea: "A classroom that exists between realities, where students are trapped",
    language: "en",
    tone: "dark"
  }
};
